import React, {useEffect, useState} from "react";
import {Command} from "cmdk";
import {
  BarChart3, Boxes, ClipboardCheck, FileText, Gauge, Home, MapPin,
  Plus, Route, Search, Settings as SettingsIcon, Terminal, User as UserIcon,
  Users,
} from "lucide-react";
import {useNavigate} from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import {cn} from "../../utils/cn";
import {hasPermission} from "../../utils/rolePermissions";
import * as Constants from "../../utils/Constants";
import {explicitLogout} from "../../component/HomeSideNavBar";

/**
 * Global command palette (Cmd/Ctrl + K).
 *
 * Lives at the App root and listens for the global keybind. Commands are
 * grouped by Pages, Quick actions, and Account. Each command honours the
 * existing hasPermission gates so users only ever see what they're
 * permitted to use.
 *
 * No business logic changes: every action either navigates to an
 * existing route or triggers a localStorage-only operation
 * (theme not yet, but reserved).
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Hide on the login / forgot-password screens — no auth means no commands
  const isAuth = !!localStorage.getItem(Constants.IS_AUTHENTICATED);

  useEffect(() => {
    if (!isAuth) return;
    const onKey = (e) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isAuth]);

  if (!isAuth) return null;

  const go = (path) => () => { setOpen(false); navigate(path); };

  // Permission-gated command catalog
  const pages = [
    {label: "Dashboard",       hint: "Home",                  icon: Home,         action: go("/home"),           perm: null},
    {label: "Live Tracking",   hint: "Map view",              icon: MapPin,       action: go("/tracking"),       perm: "sidebar_tracking"},
    {label: "Command Center",  hint: "Send device commands",  icon: Terminal,     action: go("/command-center"), perm: "sidebar_commandCenter"},
    {label: "Assets",          hint: "Trackers & locks",      icon: Boxes,        action: go("/assets"),         perm: "sidebar_assets"},
    {label: "Users",           hint: "Team & roles",          icon: Users,        action: go("/users"),          perm: "sidebar_users"},
    {label: "Tasks",           hint: "Task list",             icon: ClipboardCheck, action: go("/tasks"),        perm: "sidebar_tasks"},
    {label: "Trips",           hint: "Fleet trips",           icon: Route,        action: go("/trips"),          perm: "sidebar_trips"},
    {label: "Reports",         hint: "All reports",           icon: BarChart3,    action: go("/reports"),        perm: "sidebar_reports"},
    {label: "Audit Log",       hint: "Platform audit",        icon: FileText,     action: go("/audit-log"),      perm: "sidebar_auditLog"},
    {label: "Settings",        hint: "Account & preferences", icon: SettingsIcon, action: go("/settings"),       perm: "sidebar_settings"},
    {label: "Profile",         hint: "Your details",          icon: UserIcon,     action: go("/profile"),        perm: "sidebar_profile"},
  ].filter((p) => !p.perm || hasPermission(p.perm));

  const quickActions = [
    {label: "Register new asset", icon: Plus, action: go("/assets"),         perm: "assets_registerAsset"},
    {label: "Add new user",       icon: Plus, action: go("/users"),          perm: "users_register"},
    {label: "Create new task",    icon: Plus, action: go("/tasks"),          perm: "tasks_manage"},
    {label: "Create new trip",    icon: Plus, action: go("/trips"),          perm: "trips_manage"},
    {label: "Generate passcodes", icon: Plus, action: go("/assets"),         perm: "assets_passcode"},
    {label: "Allocate assets",    icon: Plus, action: go("/assets"),         perm: "assets_allocation"},
    {label: "Open Live Tracking", icon: MapPin, action: go("/tracking"),     perm: "sidebar_tracking"},
  ].filter((a) => !a.perm || hasPermission(a.perm));

  const account = [
    {label: "Profile", icon: UserIcon, action: go("/profile"), perm: "sidebar_profile"},
    {label: "Sign out", icon: SettingsIcon, action: () => { setOpen(false); explicitLogout(); }, perm: null},
  ].filter((a) => !a.perm || hasPermission(a.perm));

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 bg-ink-900/40 backdrop-blur-[2px] z-modal",
            "data-[state=open]:animate-fade-in",
            "data-[state=closed]:animate-fade-out"
          )}
        />
        <Dialog.Content
          aria-label="Command menu"
          className={cn(
            "fixed left-1/2 -translate-x-1/2 z-modal",
            "top-[15vh] w-[calc(100vw-2rem)] max-w-[640px]",
            "bg-white rounded-2xl shadow-xl border border-ink-200",
            "overflow-hidden focus:outline-none",
            "data-[state=open]:animate-scale-in"
          )}
        >
          <Command label="Global command menu" className="flex flex-col max-h-[70vh]">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-ink-100">
              <Search className="h-5 w-5 text-ink-400 shrink-0" />
              <Command.Input
                placeholder="Type a command or search…"
                className="flex-1 text-md text-ink-900 placeholder:text-ink-400 bg-transparent outline-none"
              />
              <kbd className="text-2xs font-mono text-ink-400 border border-ink-200 rounded px-1.5 py-0.5">Esc</kbd>
            </div>

            <Command.List className="flex-1 overflow-y-auto p-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-ink-500">
                No results.
              </Command.Empty>

              {pages.length > 0 && (
                <Command.Group heading="Pages" className="cmd-group">
                  {pages.map((p) => (
                    <CmdItem key={p.label} value={`page-${p.label}`} icon={p.icon} label={p.label} hint={p.hint} onSelect={p.action} />
                  ))}
                </Command.Group>
              )}

              {quickActions.length > 0 && (
                <Command.Group heading="Quick actions" className="cmd-group">
                  {quickActions.map((a) => (
                    <CmdItem key={a.label} value={`action-${a.label}`} icon={a.icon} label={a.label} onSelect={a.action} />
                  ))}
                </Command.Group>
              )}

              {account.length > 0 && (
                <Command.Group heading="Account" className="cmd-group">
                  {account.map((a) => (
                    <CmdItem key={a.label} value={`account-${a.label}`} icon={a.icon} label={a.label} onSelect={a.action} />
                  ))}
                </Command.Group>
              )}
            </Command.List>

            <div className="px-4 py-2 text-2xs text-ink-400 border-t border-ink-100 flex items-center justify-between">
              <span>Navigate with ↑ ↓ · Open with ↵</span>
              <span>
                <kbd className="font-mono border border-ink-200 rounded px-1 py-0.5">⌘</kbd>
                <kbd className="font-mono border border-ink-200 rounded px-1 py-0.5 ml-1">K</kbd>
              </span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CmdItem({value, icon: Icon, label, hint, onSelect}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className={cn(
        "flex items-center gap-3 px-3 h-9 rounded-md cursor-pointer",
        "text-base text-ink-700",
        "data-[selected=true]:bg-brand-50 data-[selected=true]:text-brand-700"
      )}
    >
      {Icon && <Icon className="h-4 w-4 text-ink-500" aria-hidden="true" />}
      <span className="flex-1 truncate">{label}</span>
      {hint && <span className="text-xs text-ink-400">{hint}</span>}
    </Command.Item>
  );
}
